import { useCallback } from 'react'

import { useTranslations } from 'next-intl'

import { indexes } from '@mab-common/utils'

import classnames from 'classnames'

import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import Button, { ButtonVariants } from '~/components/atoms/Button/Button'
import CollapsibleReadMore from '~/components/atoms/CollapsibleReadMore/CollapsibleReadMore'
import Container from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import { Col, Grid, Row } from '~/components/atoms/Grid'
import Spacer, { SpacerSizes } from '~/components/atoms/Spacer/Spacer'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'
import useSearchAnalyst, {
  SearchAnalystEventNames,
  SearchAnalystMethods,
} from '~/services/elastic/analyst'
import type { Block } from '~/services/sanity'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import { multiPostHogEventPusher } from '~/utils/analyticsPusher'

import Accordion from '../Accordion/Accordion'

import styles from './CatalogueEssay.module.css'
import RecordTypes = indexes.RecordTypes
import EsIndexTypes = indexes.EsIndexTypes

export interface CatalogueEssayProps {
  title: string
  intro: Block[]
  content: Block[]
  artist?: {
    name?: string
    about?: Block[]
    includeAboutArtist?: boolean
  }
  links?: {
    url: string
    linkText: string
    type: RecordTypes
    id: string
  }[]
  testID?: string
  valueText?: Block[]
}

export default function CatalogueEssay({
  artist,
  title,
  intro,
  content,
  links,
  testID,
  valueText,
}: CatalogueEssayProps) {
  const tCommon = useTranslations('common')
  const { sendSearchInsightsEvent } = useSearchAnalyst()

  const isDesktop = useMediaQuery(`(min-width: ${bp.bpMediumMax})`)

  const onLinkClick = useCallback(async (link: any) => {
    const { id, type } = link

    const eventName =
      type === RecordTypes.ARTIST
        ? SearchAnalystEventNames.artworkArtistClicked
        : SearchAnalystEventNames.artworkCollectionClicked
    const indexType =
      type === RecordTypes.ARTIST ? EsIndexTypes.ARTIST : EsIndexTypes.ARTWORKS

    sendSearchInsightsEvent(SearchAnalystMethods.clickedFilters, {
      eventName,
      indexType,
      filters: [`${type}Id:${id}`],
    })

    await multiPostHogEventPusher([
      {
        eventType: eventName,
      },
    ])
  }, [])

  const accordionData = []

  if (valueText) {
    accordionData.push({
      title: tCommon('valueTrend'),
      _key: 'valuetext',
      body: valueText,
    })
  }

  if (artist?.about) {
    accordionData.push({
      title: `${tCommon('about')} ${artist.name}`,
      _key: 'aboutartisttext',
      body: artist.about,
    })
  }

  const renderLinks = () => {
    return (
      <>
        {links &&
          links.map((link, idx) => (
            <div className={styles.link} key={`${link.url}-${idx}`}>
              <Button
                link={link?.url}
                variant={ButtonVariants.secondaryDark}
                inline={true}
                iconLeft={true}
                linkOnclick={() => onLinkClick(link)}
              >
                {link?.linkText}
              </Button>
            </div>
          ))}
      </>
    )
  }

  return (
    <div className={`${styles.base}`} data-testid={testID}>
      {/* Figma: the Meaning & Analysis section sits on a white background */}
      <Fixture theme={ComponentTheme.primary} verticalPadding={true}>
        <Container>
          <Text
            size={TextSizes.primaryTitleLrg}
            Tag="h2"
            className={styles.title}
          >
            {title}
          </Text>
          <Grid>
            <Row>
              <Col variant="col-1/2--lg">
                <div
                  className={classnames(styles.introInner, {
                    [styles.introWithLinks]: links,
                  })}
                >
                  <CollapsibleReadMore theme={ComponentTheme.primary}>
                    <span
                      className={classnames(
                        styles.intro,
                        'json-ld-speakable',
                        'prose'
                      )}
                    >
                      <BlockContent
                        value={intro}
                        size={TextSizes.secondaryTitleMedReg}
                      ></BlockContent>
                      {!isDesktop && (
                        <div className={classnames(styles.content, 'prose')}>
                          <BlockContent value={content}></BlockContent>
                        </div>
                      )}
                    </span>
                  </CollapsibleReadMore>
                </div>
                <div className={styles.linksMd}>{renderLinks()}</div>
              </Col>
              <Col variant="col-1/2--lg">
                {isDesktop && (
                  <div className={classnames(styles.content, 'prose')}>
                    <BlockContent value={content}></BlockContent>
                  </div>
                )}
                <Spacer size={SpacerSizes.sm} />

                <div className={styles.linksSm}>{renderLinks()}</div>

                {accordionData.length ? (
                  <Accordion data={accordionData} noContainer firstItemOpen />
                ) : null}
              </Col>
            </Row>
          </Grid>
        </Container>
      </Fixture>
    </div>
  )
}
